import { ReactElement, useState } from 'react';
import { resolveValue, Toast, toast as ToastInstance, useToaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { ToastErrorIcon, ToastSuccessIcon } from '../../../assets';
import { CloseIcon } from '../../../assets';
import { BOTTOM_BAR_ELEMENT_ID } from '../../../components/layout/BottomNav/BottomNav';
import { MAX_AMOUNT_OF_NOTIFICATIONS_IN_STACK } from '../CustomToaster';
import {
  CloseButton,
  Container,
  ContentContainer,
  DismissButton,
  IconContainer,
  MessageContainer,
  MessageText,
  MoreNotificationsText,
  RightContainer,
} from './CustomToast.styles';

const SPACE_BETWEEN_TOASTS = 8;

interface CustomToastProps {
  toast: Toast;
  index: number;
  active?: boolean;
  totalAmount?: number;
}

export const NotificationsCount = ({ totalAmount }: { totalAmount: number }) => {
  const { t } = useTranslation();

  return (
    <MoreNotificationsText>
      {totalAmount >= MAX_AMOUNT_OF_NOTIFICATIONS_IN_STACK
        ? t('notifications.xMoreNotificationsPlural', { notificationsCount: totalAmount - 1 })
        : t('notifications.xMoreNotificationsSingular', { notificationsCount: totalAmount - 1 })}
    </MoreNotificationsText>
  );
};

const CustomToast = ({ toast, index, active, totalAmount }: CustomToastProps) => {
  const { t } = useTranslation();
  const { handlers, toasts } = useToaster();
  const { updateHeight } = handlers;

  // animate if a notification is a new one
  const [shouldAnimate, setShouldAnimate] = useState(!toast.height);
  // we shold keep local state var to avoid changing a number in all notifications
  const [totalAmountLocal] = useState(totalAmount);

  const isCustom = toast.type === 'custom';

  const ref = (el: HTMLDivElement) => {
    if (active && el && typeof toast.height !== 'number') {
      const height = el.getBoundingClientRect().height;
      updateHeight(toast.id, height);
    }
  };

  const icon = (
    <IconContainer $type={toast.type}>
      {toast.type === 'error' ? <ToastErrorIcon /> : <ToastSuccessIcon />}
    </IconContainer>
  );

  const dismissToast = () => {
    ToastInstance.dismiss(toast.id);
  };

  const clearAll = () => toasts.forEach((toast) => ToastInstance.dismiss(toast.id));

  const renderCustomComponent = () => {
    const component = resolveValue(toast.message, toast) as ReactElement;
    const CustomComponent = component.type;
    const customComponentProps = {
      ...component.props,
      dateCreatedAt: !totalAmount && component.props.dateCreatedAt,
      onDismiss: () => {
        dismissToast();
        component.props.onDismiss?.();
      },
      infoComponent: totalAmount && <NotificationsCount totalAmount={totalAmount} />,
      onSubmit: () => {
        dismissToast();
        component.props.onSubmit?.();
      },
    };
    return (
      <div>
        <CustomComponent {...customComponentProps} />
      </div>
    );
  };

  return (
    <Container
      key={toast.id}
      ref={ref}
      $visible={toast.visible}
      $active={active}
      $offsetY={SPACE_BETWEEN_TOASTS * index}
      $animated={!toast.visible || shouldAnimate}
      $isBottomBarVisible={!!document.getElementById(BOTTOM_BAR_ELEMENT_ID)}
      onAnimationEnd={() => setShouldAnimate(false)}
      {...toast.ariaProps}
    >
      <ContentContainer $isCustom={isCustom}>
        {isCustom ? (
          renderCustomComponent()
        ) : (
          <>
            <MessageContainer>
              {icon}
              <RightContainer>
                <MessageText>{resolveValue(toast.message, toast)}</MessageText>
                {totalAmountLocal && <NotificationsCount totalAmount={totalAmountLocal} />}
                <DismissButton onClick={dismissToast}>{t('subscriptions.dismiss')}</DismissButton>
              </RightContainer>
            </MessageContainer>
          </>
        )}
        {active && (
          <CloseButton $expandable={toasts.length > 1} onClick={clearAll}>
            <CloseIcon />
            <span>{t('filters.clearAll')}</span>
          </CloseButton>
        )}
      </ContentContainer>
    </Container>
  );
};

export default CustomToast;
